import { useState } from 'react'
import { useResolvedLanguages } from '@/components/common/translations/useAutoTranslate.ts'
import type {PlannedLanguages, TranslationMap} from '@/components/common/translations/types.ts'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button.tsx'
import { Languages } from 'lucide-react'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/shadcn/dialog'

export interface PropertyPlanConfig {
  property: string
  sourceValue: string
  existingTranslations: TranslationMap
  manualTranslations: TranslationMap
  plannedLanguages?: PlannedLanguages
  onPlannedChange?: (planned: PlannedLanguages) => void
}

export interface AutoTranslateAllButtonProps {
  properties: PropertyPlanConfig[]
  autoTranslateAll: boolean
  onAutoTranslateAllChange: (value: boolean) => void
  disabled?: boolean
}

export function AutoTranslateAllButton({
                                         properties,
                                         autoTranslateAll,
                                         onAutoTranslateAllChange,
                                         disabled = false,
                                       }: AutoTranslateAllButtonProps) {
  const { t } = useTranslation()
  const languages = useResolvedLanguages()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const isTranslated = (p: PropertyPlanConfig, code: string) =>
    !!p.existingTranslations[code]?.trim() || !!p.manualTranslations[code]?.trim()

  const hasAnythingToTranslate = properties.some(
    (p) => p.sourceValue.trim() && languages.some((l) => !isTranslated(p, l.code)),
  )

  return (
    <>
      <Button
        type="button"
        variant={autoTranslateAll ? 'default' : 'outline'}
        size="sm"
        onClick={() => autoTranslateAll ? onAutoTranslateAllChange(false) : setConfirmOpen(true)}
        disabled={disabled || (!autoTranslateAll && !hasAnythingToTranslate)}
      >
        <Languages className="mr-2 h-4 w-4" />
        {autoTranslateAll
          ? t('common:translations.autoUnplanAll')
          : t('common:translations.autoTranslateAllProperties')
        }
      </Button>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('common:translations.autoConfirmTitle')}</DialogTitle>
            <DialogDescription>{t('common:translations.autoConfirmDescription')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmOpen(false)}
            >
              {t('common:buttons.cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => {
                onAutoTranslateAllChange(true)
                setConfirmOpen(false)
              }}
            >
              {t('common:translations.autoConfirmAction')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
